import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import {
  FileText,
  Lightbulb,
  AlertCircle,
  Calendar,
  CalendarClock,
  Clock,
  MapPin,
  Pencil,
  Repeat,
  UserRound,
} from 'lucide-react'
import {
  usePatientDetail,
  usePatientReports,
} from '../hooks/use-patient-detail'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useRecurringSchedules } from '@/features/scheduling/hooks/use-recurring-schedules'
import { useOneOffAppointments } from '@/features/scheduling/hooks/use-appointments'
import {
  DAY_OF_WEEK_LABELS,
  FREQUENCY_LABELS,
  SESSION_STATUS_LABELS,
  type RecurringSchedule,
  type Session,
} from '@/features/scheduling/types/scheduling.types'

interface PatientOverviewTabProps {
  patientId: string
  onEditSchedule?: () => void
}

const INITIAL_VISIBLE_SCHEDULES = 3
const SCHEDULES_PAGE_SIZE = 3

function formatDateLabel(date: string | null | undefined) {
  if (!date) return null
  const [year, month, day] = date.split('-')
  if (!year || !month || !day) return date
  return `${day}/${month}/${year}`
}

function formatTimeLabel(time: string | null | undefined) {
  if (!time) return '--:--'
  return time.slice(0, 5)
}

function formatTimeRange(
  startTime: string | null | undefined,
  endTime: string | null | undefined,
) {
  return `${formatTimeLabel(startTime)} — ${formatTimeLabel(endTime)}`
}

function getTodayISO() {
  return new Date().toLocaleDateString('en-CA')
}

function getRecurringScheduleState(schedule: RecurringSchedule) {
  const today = getTodayISO()

  if (schedule.effective_until && schedule.effective_until < today) {
    return {
      label: 'Encerrado',
      className: 'border-amber-200 bg-amber-50 text-amber-700',
      detail: `Terminou em ${formatDateLabel(schedule.effective_until)}.`,
      countsAsActive: false,
    }
  }

  if (schedule.effective_from > today) {
    return {
      label: 'Programado',
      className: 'border-blue-200 bg-blue-50 text-blue-700',
      detail: `Começa em ${formatDateLabel(schedule.effective_from)}.`,
      countsAsActive: true,
    }
  }

  return {
    label: schedule.effective_until ? 'Vigente' : 'Sem término',
    className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    detail: schedule.effective_until
      ? `Até ${formatDateLabel(schedule.effective_until)}.`
      : null,
    countsAsActive: true,
  }
}

function getRecurringProfessionalNames(schedule: RecurringSchedule) {
  return (schedule.professionals ?? [])
    .map((item) => item.professional?.full_name)
    .filter(Boolean)
    .join(', ')
}

function getAppointmentProfessionalNames(appointment: Session) {
  return [
    appointment.primary_professional?.full_name,
    ...(appointment.additional_professionals ?? []).map(
      (item) => item.professional?.full_name,
    ),
  ]
    .filter(Boolean)
    .join(', ')
}

export function PatientOverviewTab({
  patientId,
  onEditSchedule,
}: PatientOverviewTabProps) {
  const [visibleScheduleCount, setVisibleScheduleCount] = useState(
    INITIAL_VISIBLE_SCHEDULES,
  )
  const { data: patient, isLoading: patientLoading } =
    usePatientDetail(patientId)
  const { data: recentReports, isLoading: reportsLoading } =
    usePatientReports(patientId)
  const { data: recurringSchedules, isLoading: schedulesLoading } =
    useRecurringSchedules(patientId)
  const { data: oneOffAppointments, isLoading: appointmentsLoading } =
    useOneOffAppointments(patientId)

  useEffect(() => {
    setVisibleScheduleCount(INITIAL_VISIBLE_SCHEDULES)
  }, [patientId])

  if (patientLoading) {
    return <OverviewSkeleton />
  }

  if (!patient) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Paciente não encontrado
      </div>
    )
  }

  const activeRecurringSchedules = (recurringSchedules ?? []).filter(
    (schedule) => schedule.is_active,
  )
  const upcomingOneOffAppointments = oneOffAppointments ?? []
  const activeScheduleCount =
    activeRecurringSchedules.filter(
      (schedule) => getRecurringScheduleState(schedule).countsAsActive,
    ).length + upcomingOneOffAppointments.length
  const scheduleItemCount =
    activeRecurringSchedules.length + upcomingOneOffAppointments.length
  const visibleRecurringSchedules = activeRecurringSchedules.slice(
    0,
    visibleScheduleCount,
  )
  const visibleOneOffAppointments =
    visibleScheduleCount > activeRecurringSchedules.length
      ? upcomingOneOffAppointments.slice(
          0,
          visibleScheduleCount - activeRecurringSchedules.length,
        )
      : []
  const displayedScheduleCount =
    visibleRecurringSchedules.length + visibleOneOffAppointments.length
  const hiddenScheduleCount = Math.max(
    scheduleItemCount - displayedScheduleCount,
    0,
  )
  const isScheduleLoading = schedulesLoading || appointmentsLoading
  const hasScheduleItems =
    activeRecurringSchedules.length > 0 || upcomingOneOffAppointments.length > 0

  const stats = [
    {
      label: 'Total de relatos',
      value: patient._count.reports,
      icon: FileText,
      color: 'text-blue-600',
    },
    {
      label: 'Agenda ativa',
      value: isScheduleLoading ? '...' : activeScheduleCount,
      icon: CalendarClock,
      color: 'text-teal-600',
    },
    {
      label: 'Alertas',
      value: patient._count.insights,
      icon: Lightbulb,
      color: 'text-yellow-600',
    },
    {
      label: 'Análises semanais',
      value: patient._count.summaries,
      icon: Calendar,
      color: 'text-green-600',
    },
  ]

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg bg-muted ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Patient Schedule */}
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-lg">Agenda do paciente</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Recorrências cadastradas e próximos atendimentos avulsos.
            </p>
            {hasScheduleItems && !isScheduleLoading && (
              <p className="mt-1 text-xs text-muted-foreground">
                Exibindo {displayedScheduleCount} de {scheduleItemCount}
              </p>
            )}
          </div>
          {onEditSchedule && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onEditSchedule}
              className="w-full sm:w-auto"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Editar agenda
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isScheduleLoading ? (
            <div className="grid gap-3 lg:grid-cols-2">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : hasScheduleItems ? (
            <div className="space-y-5">
              {visibleRecurringSchedules.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Recorrentes
                  </p>
                  <div className="grid gap-3 lg:grid-cols-2">
                    {visibleRecurringSchedules.map((schedule) => {
                      const scheduleState = getRecurringScheduleState(schedule)
                      const professionalNames =
                        getRecurringProfessionalNames(schedule)

                      return (
                        <div
                          key={schedule.id}
                          className="rounded-lg border bg-muted/20 p-4"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <Repeat className="h-4 w-4 text-muted-foreground" />
                                <p className="font-medium">
                                  {DAY_OF_WEEK_LABELS[schedule.day_of_week]}
                                </p>
                              </div>
                              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                                <span className="inline-flex items-center gap-1">
                                  <Clock className="h-3.5 w-3.5" />
                                  {formatTimeRange(
                                    schedule.start_time,
                                    schedule.end_time,
                                  )}
                                </span>
                                <Badge variant="secondary">
                                  {FREQUENCY_LABELS[schedule.frequency]}
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className={scheduleState.className}
                                >
                                  {scheduleState.label}
                                </Badge>
                              </div>
                            </div>
                          </div>

                          <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                            <p>
                              Início: {formatDateLabel(schedule.effective_from)}
                              {' • '}
                              Término:{' '}
                              {formatDateLabel(schedule.effective_until) ??
                                'sem data'}
                            </p>
                            {scheduleState.detail && (
                              <p className="text-amber-700">
                                {scheduleState.detail}
                              </p>
                            )}
                            {professionalNames && (
                              <p className="flex items-center gap-1">
                                <UserRound className="h-3.5 w-3.5" />
                                <span>{professionalNames}</span>
                              </p>
                            )}
                            {schedule.location_text && (
                              <p className="flex items-center gap-1">
                                <MapPin className="h-3.5 w-3.5" />
                                <span>{schedule.location_text}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {visibleOneOffAppointments.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Avulsos futuros
                  </p>
                  <div className="grid gap-3 lg:grid-cols-2">
                    {visibleOneOffAppointments.map((appointment) => {
                      const professionalNames =
                        getAppointmentProfessionalNames(appointment)

                      return (
                        <div
                          key={appointment.id}
                          className="rounded-lg border bg-muted/20 p-4"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <CalendarClock className="h-4 w-4 text-muted-foreground" />
                            <p className="font-medium">
                              {formatDateLabel(appointment.session_date)}
                            </p>
                            <Badge variant="outline">
                              {SESSION_STATUS_LABELS[appointment.status]}
                            </Badge>
                          </div>
                          <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                            <p className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              <span>
                                {formatTimeRange(
                                  appointment.start_time,
                                  appointment.end_time,
                                )}
                              </span>
                            </p>
                            {professionalNames && (
                              <p className="flex items-center gap-1">
                                <UserRound className="h-3.5 w-3.5" />
                                <span>{professionalNames}</span>
                              </p>
                            )}
                            {appointment.location_text && (
                              <p className="flex items-center gap-1">
                                <MapPin className="h-3.5 w-3.5" />
                                <span>{appointment.location_text}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {(hiddenScheduleCount > 0 ||
                displayedScheduleCount > INITIAL_VISIBLE_SCHEDULES) && (
                <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-muted-foreground">
                    {hiddenScheduleCount > 0
                      ? `${hiddenScheduleCount} agendamento(s) oculto(s)`
                      : 'Todos os agendamentos estão visíveis'}
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    {displayedScheduleCount > INITIAL_VISIBLE_SCHEDULES && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setVisibleScheduleCount(INITIAL_VISIBLE_SCHEDULES)
                        }
                      >
                        Mostrar menos
                      </Button>
                    )}
                    {hiddenScheduleCount > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setVisibleScheduleCount((current) =>
                            Math.min(
                              current + SCHEDULES_PAGE_SIZE,
                              scheduleItemCount,
                            ),
                          )
                        }
                      >
                        Carregar mais
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
              <CalendarClock className="mx-auto mb-2 h-8 w-8 opacity-60" />
              <p className="font-medium text-foreground">
                Nenhum agendamento ativo
              </p>
              <p className="mt-1">
                Use a edição da agenda para cadastrar recorrências ou avulsos.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Patient Info */}
      {patient.medical_history && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Histórico médico</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground whitespace-pre-wrap">
              {patient.medical_history}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Family Members */}
      {patient.family_members.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Familiares</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {patient.family_members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-3 bg-muted rounded-lg"
                >
                  <div>
                    <p className="font-medium">{member.profiles?.full_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {member.relationship}
                      {(member.whatsapp_number || member.profiles?.phone) &&
                        ` • ${member.whatsapp_number || member.profiles?.phone}`}
                    </p>
                  </div>
                  {member.is_primary_contact && (
                    <Badge variant="secondary">Contato Principal</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Reports */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Relatos recentes</CardTitle>
        </CardHeader>
        <CardContent>
          {reportsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : recentReports && recentReports.length > 0 ? (
            <div className="space-y-3">
              {recentReports.slice(0, 5).map((report) => (
                <div
                  key={report.id}
                  className="p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-sm line-clamp-2">{report.content}</p>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDistanceToNow(new Date(report.created_at), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Nenhum relato encontrado</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function OverviewSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <Skeleton className="h-16 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    </div>
  )
}
